import { useEffect, useState } from 'react'
import { modelAssetUrl } from '../lib/model-assets'

/**
 * Interactive 3D preview via <model-viewer> (modelviewer.dev).
 *
 * The library registers a custom element, so it's loaded once on the
 * client — importing it during SSR would touch `self`/`window` and
 * crash the render. Until it's ready the frame shows the quiet
 * loading state, matching the v2 media frame it sits inside.
 */
export function ModelViewer({
  src,
  alt,
  poster,
  /**
   * model-viewer frames the bounding *sphere*, which leaves a wide flat
   * object marooned in empty space — hence the pulled-in radius. The
   * polar angle favours the top face, where the paint detail lives.
   */
  cameraOrbit = '25deg 62deg 82%',
  /**
   * Scanned objects have no underside — the scanner can't see the face
   * the object rests on. Clamping the polar angle to the horizon keeps
   * the open bottom out of frame, which is cheaper and cleaner than
   * modelling a cap the customer would never look at anyway.
   */
  maxCameraOrbit = 'Infinity 90deg auto',
}: {
  src: string
  alt: string
  poster?: string
  cameraOrbit?: string
  maxCameraOrbit?: string
}) {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    import('@google/model-viewer').then(() => {
      if (!cancelled) setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!ready) {
    return (
      <div className="flex size-full items-center justify-center text-[13px] text-ink-faint">
        Loading 3D view…
      </div>
    )
  }

  return (
    <model-viewer
      src={modelAssetUrl(src)}
      alt={alt}
      poster={poster}
      camera-controls
      auto-rotate
      auto-rotate-delay={3000}
      rotation-per-second="24deg"
      touch-action="pan-y"
      interaction-prompt="auto"
      shadow-intensity={1}
      shadow-softness={0.8}
      exposure={1}
      camera-orbit={cameraOrbit}
      max-camera-orbit={maxCameraOrbit}
      style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
    />
  )
}
